/**
 * Preview metadata uses sessionStorage; sensitive DQ documents stay in browser memory only.
 */

export interface PreviewData {
    previewId: string;
    previewHtmlFileToken?: string | null;
    encryptedApplicationId: string;
    previewHtmlContentHash?: string | null;
    /** Base64 DQ document, memory-only. Undefined requires regeneration; null means no DQ. */
    demographicQuestionAnswersHTML?: string | null;
}

interface PreviewCache {
    [draftId: string]: PreviewData;
}

const CACHE_KEY = 'application_previews';
const demographicPreviews = new Map<string, PreviewData>();

/**
 * Get all cached preview data
 */
function getCache(): PreviewCache {
    if (typeof window === 'undefined') {
        return {};
    }

    try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (!cached) return {};
        return JSON.parse(cached) as PreviewCache;
    } catch {
        // Clear corrupted cache data
        sessionStorage.removeItem(CACHE_KEY);
        return {};
    }
}

/**
 * Save entire cache to sessionStorage
 */
function saveCache(cache: PreviewCache): void {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
        // Clear cache if quota exceeded or other storage error
        console.warn('sessionStorage error, clearing cache', error);
        sessionStorage.removeItem(CACHE_KEY);
    }
}

/**
 * Validate that cached data has the correct structure
 */
function validatePreviewData(data: unknown): data is PreviewData {
    return (
        data !== null &&
        typeof data === 'object' &&
        'previewId' in data &&
        'encryptedApplicationId' in data &&
        typeof data.previewId === 'string' &&
        typeof data.encryptedApplicationId === 'string'
    );
}

/**
 * Get cached preview data for a specific draft
 * Returns null if not cached or if data is invalid
 */
export function getCachedPreview(draftId: string): PreviewData | null {
    const cache = getCache();
    const data = cache[draftId];

    if (!data) {
        return null;
    }

    // Validate the cached data structure
    if (!validatePreviewData(data)) {
        // Remove invalid data from cache
        demographicPreviews.delete(draftId);
        delete cache[draftId];
        saveCache(cache);
        return null;
    }

    const demographicPreview = demographicPreviews.get(draftId);
    if (demographicPreview?.previewId === data.previewId) {
        return {
            ...data,
            demographicQuestionAnswersHTML: demographicPreview.demographicQuestionAnswersHTML,
        };
    }

    return data;
}

/**
 * Set cached preview data for a specific draft
 */
export function setCachedPreview(draftId: string, data: PreviewData): void {
    if (typeof window === 'undefined') return;

    const cache = getCache();
    demographicPreviews.delete(draftId);
    if (data.demographicQuestionAnswersHTML) {
        demographicPreviews.set(draftId, data);
        // After reload this field is undefined, so Review regenerates instead of hiding DQ.
        cache[draftId] = { ...data, demographicQuestionAnswersHTML: undefined };
    } else {
        cache[draftId] = data;
    }
    saveCache(cache);
}

/**
 * Remove cached preview data for a specific draft
 */
export function deleteCachedPreview(draftId: string): void {
    demographicPreviews.delete(draftId);
    const cache = getCache();
    if (draftId in cache) {
        delete cache[draftId];
        saveCache(cache);
    }
}

/**
 * Remove cached preview data for drafts not in the provided list
 */
export function cleanupStaleCache(validDraftIds: string[]): void {
    const cache = getCache();
    const cachedIds = Object.keys(cache);
    const validSet = new Set(validDraftIds);

    for (const draftId of demographicPreviews.keys()) {
        if (!validSet.has(draftId)) demographicPreviews.delete(draftId);
    }

    let hasChanges = false;
    for (const cachedId of cachedIds) {
        if (!validSet.has(cachedId)) {
            delete cache[cachedId];
            hasChanges = true;
        }
    }

    if (hasChanges) {
        saveCache(cache);
    }
}
